import classNames from 'classnames';
import dayjs from 'dayjs';
import { useCallback, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import CreateQuickTaskModal from '@/modules/admin-dashboard/components/modals/CreateQuickTaskModal.tsx';
import FormTagsModal from '@/modules/admin-dashboard/components/modals/FormTagsModal.tsx';
import {
  useCreateFormMutation,
  useDeleteFormMutation,
  useRestoreFormMutation,
} from '@/modules/admin-dashboard/queries/forms.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import {
  ConfirmModal,
  ConfirmModalProps,
  DisclosureListItem,
  DropdownMenu,
  ModalProviderContext,
} from 'ui';
import { DotsHorizontalIcon, SendGreyIcon } from 'ui/icons';
import { FormResponse } from 'compliance-api';

interface FormItemProps {
  form: FormResponse;
}

export const FormItem = ({ form }: FormItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const navigate = useNavigate();

  const { name, created_at, deleted_at, id, data, created_with_ai } = form;

  const { mutateAsync: deleteForm } = useDeleteFormMutation();
  const { mutateAsync: duplicateForm } = useCreateFormMutation();

  const handleDeleteForm = useCallback(async () => {
    await deleteForm(id);
  }, [deleteForm, id]);

  const handleDuplicateForm = useCallback(async () => {
    await duplicateForm({
      name: name + ' Duplicated',
      data,
    });
  }, [data, duplicateForm, name]);

  const title = useMemo(
    () => (
      <Link
        className="hover:underline"
        to={{ pathname: adminDashboardPaths.dynamic.form(id), search: location.search }}
        replace={false}
      >
        {name}
      </Link>
    ),
    [id, name],
  );

  const handleOpenCreateQuickTaskModal = useCallback(() => {
    openModal(CreateQuickTaskModal, { formId: form.id });
  }, [form.id, openModal]);

  const dropdownMenuOptions = useMemo(
    () => [
      {
        label: 'Edit',
        onClick: () =>
          navigate(
            { pathname: adminDashboardPaths.dynamic.form(id), search: location.search },
            { replace: false },
          ),
      },
      {
        label: 'Categories',
        onClick: () => openModal(FormTagsModal, { form }),
      },
      {
        label: 'Duplicate',
        onClick: () =>
          openModal<ConfirmModalProps>(ConfirmModal, {
            onConfirm: handleDuplicateForm,
            title: (
              <p>
                Are you sure you want to duplicate <span className="text-blue-700">{name}</span> ?
              </p>
            ),
          }),
      },
      {
        label: 'Archive',
        onClick: () =>
          openModal<ConfirmModalProps>(ConfirmModal, {
            onConfirm: handleDeleteForm,
            title: (
              <p>
                Are you sure you want to archive form <span className="text-blue-700">{name}</span>{' '}
                ?
              </p>
            ),
          }),
      },
    ],
    [form, handleDeleteForm, handleDuplicateForm, id, name, navigate, openModal],
  );

  return (
    <DisclosureListItem
      leftMainString={title}
      leftSubduedContentSet={
        created_with_ai
          ? [
              `Created: ${dayjs(created_at).format('DD-MM-YYYY')}`,
              `${form.reference_name}`,
              <CreatedByAiLabel created_with_ai={created_with_ai} />,
            ]
          : [`Created: ${dayjs(created_at).format('DD-MM-YYYY')}`, `${form.reference_name}`]
      }
      classes={{
        container: classNames({ 'bg-gray-100': deleted_at }),
        content: 'grid grid-cols-[1fr_auto]',
        leftContent: '[&>div]:w-full',
        rightMainString: '!w-auto',
      }}
      rightFlexStartSubduedString={
        !deleted_at && (
          <DropdownMenu
            autoPlace
            items={dropdownMenuOptions}
            button={
              <button className="flex items-center justify-center">
                <img src={DotsHorizontalIcon} alt="Loading..." />
              </button>
            }
            className="w-[180px] divide-white"
          />
        )
      }
      rightContent={
        deleted_at ? (
          <ArchivedItemAction id={id} mutation={useRestoreFormMutation} />
        ) : (
          <button onClick={handleOpenCreateQuickTaskModal}>
            <img src={SendGreyIcon} alt="send quick task" />
          </button>
        )
      }
    />
  );
};
